import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from './ui/button'

export const EMPTY_CUSTOMER_LIST_FILTERS = Object.freeze({
  region: '',
  ageBand: '',
  source: '',
  segment: '',
  activeOnly: true,
})

export const DEFAULT_CUSTOMER_AGE_BAND_OPTIONS = [
  { value: 'UNDER_30', label: '\u0032\u0030\uB300 \uC774\uD558' },
  { value: 'AGE_30S', label: '\u0033\u0030\uB300' },
  { value: 'AGE_40S', label: '\u0034\u0030\uB300' },
  { value: 'AGE_50S', label: '\u0035\u0030\uB300' },
  { value: 'AGE_60_PLUS', label: '\u0036\u0030\uB300 \uC774\uC0C1' },
]

function copyFilters(filters) {
  return {
    region: String(filters?.region || ''),
    ageBand: String(filters?.ageBand || ''),
    source: String(filters?.source || ''),
    segment: String(filters?.segment || ''),
    activeOnly: filters?.activeOnly !== false,
  }
}

export function activeCustomerListFilterCount(filters) {
  const normalized = copyFilters(filters)
  return [normalized.region, normalized.ageBand, normalized.source, normalized.segment].filter(Boolean).length
}

export function customerListFilterParams(filters) {
  const normalized = copyFilters(filters)
  return {
    activeOnly: normalized.activeOnly,
    ...Object.fromEntries(
      Object.entries(normalized).filter(([key, value]) => key !== 'activeOnly' && value),
    ),
  }
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="grid min-w-0 gap-1.5 text-xs font-semibold text-slate-700">
      <span>{label}</span>
      <select
        value={value}
        className="h-10 min-w-0 rounded-md border border-sgez-border-strong bg-sgez-surface px-3 text-sm font-medium text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{'\uC804\uCCB4'}</option>
        {(Array.isArray(options) ? options : []).map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  )
}

export default function CustomerListFilterDialog({ open, filters, options, onApply, onReset, onClose }) {
  const [draft, setDraft] = useState(() => copyFilters(filters))

  useEffect(() => {
    if (open) setDraft(copyFilters(filters))
  }, [filters, open])

  if (!open) return null

  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }))

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/45"
        aria-label={'\uD544\uD130 \uB2EB\uAE30'}
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-list-filter-title"
        className="relative z-10 w-full max-w-xl overflow-hidden rounded-lg border border-sgez-border bg-sgez-surface shadow-2xl"
      >
        <header className="flex h-14 items-center justify-between border-b border-sgez-border px-5">
          <h2 id="customer-list-filter-title" className="text-base font-bold text-slate-900">{'\uACE0\uAC1D \uD544\uD130'}</h2>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-sgez-surface-subtle hover:text-slate-800"
            title={'\uB2EB\uAE30'}
            aria-label={'\uB2EB\uAE30'}
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <FilterSelect label={'\uC9C0\uC5ED'} value={draft.region} options={options?.regions} onChange={(value) => update('region', value)} />
          <FilterSelect label={'\uC5F0\uB839'} value={draft.ageBand} options={options?.ageBands} onChange={(value) => update('ageBand', value)} />
          <FilterSelect label={'\uCD9C\uCC98'} value={draft.source} options={options?.sources} onChange={(value) => update('source', value)} />
          <FilterSelect label={'\uAD6C\uBD84'} value={draft.segment} options={options?.segments} onChange={(value) => update('segment', value)} />
          <label className="flex min-h-10 items-center gap-2 rounded-md border border-sgez-border bg-sgez-surface-subtle px-3 text-sm font-semibold text-slate-800 sm:col-span-2">
            <input
              type="checkbox"
              checked={draft.activeOnly}
              className="h-4 w-4 rounded border-slate-300 text-brand-navy focus:ring-brand-sky"
              onChange={(event) => update('activeOnly', event.target.checked)}
            />
            <span>{'\uD65C\uC131 \uACE0\uAC1D\uB9CC \uBCF4\uAE30'}</span>
          </label>
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-sgez-border bg-sgez-surface-subtle px-5 py-3">
          <Button
            type="button"
            variant="sgezOutline"
            size="sm"
            onClick={() => {
              onReset?.()
              onClose?.()
            }}
          >
            {'\uC804\uCCB4 \uCD08\uAE30\uD654'}
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="sgezOutline" size="sm" onClick={onClose}>{'\uCDE8\uC18C'}</Button>
            <Button
              type="button"
              variant="sgez"
              size="sm"
              onClick={() => {
                onApply?.(copyFilters(draft))
                onClose?.()
              }}
            >
              {'\uC801\uC6A9'}
            </Button>
          </div>
        </footer>
      </section>
    </div>
  )
}